/**
 * FIDDUX — Biblioteca de Reports
 * Lista, busca e filtra os reports gerados (PDF e áudio) salvos no Storage e
 * registrados em Firestore (`admin_pdf_reports`). Responsivo (tabela no desktop,
 * cards no mobile).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Card,
  CardContent,
  Stack,
  Button,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import RefreshIcon from '@mui/icons-material/Refresh';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { DATABASE } from '../../../Config/firebase';
import { FIDDUX_BRANDING } from '../../../Utils/pdfReportBranding';
import { AUDIENCE_LABELS } from '../../../Utils/pdfReportTemplates';

const audienceColor = (aud) => (aud === 'internal' ? 'warning' : 'success');

function toDate(ts) {
  try {
    return ts?.toDate?.() || (ts ? new Date(ts) : null);
  } catch {
    return null;
  }
}

export default function ReportLibrary() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [search, setSearch] = useState('');
  const [fAudience, setFAudience] = useState('all');
  const [fType, setFType] = useState('all');
  const [fLang, setFLang] = useState('all');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(DATABASE, 'admin_pdf_reports'),
        orderBy('generated_at', 'desc'),
        limit(500)
      );
      const snap = await getDocs(q);
      setReports(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.warn('Falha ao carregar reports:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const types = useMemo(() => {
    const set = new Set();
    reports.forEach((r) => r.report_type && set.add(r.report_type));
    return Array.from(set);
  }, [reports]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const from = fFrom ? new Date(fFrom) : null;
    const to = fTo ? new Date(fTo) : null;
    return reports.filter((r) => {
      if (term) {
        const hay = `${r.title || ''} ${r.title_pt || ''} ${r.filename || ''} ${(r.tags || []).join(' ')}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (fAudience !== 'all' && (r.audience || '') !== fAudience) return false;
      if (fType !== 'all' && (r.report_type || '') !== fType) return false;
      if (fLang !== 'all' && (r.language || '').toLowerCase() !== fLang) return false;
      const d = toDate(r.generated_at) || (r.date ? new Date(r.date) : null);
      if (from && d && d < from) return false;
      if (to && d) {
        const end = new Date(to);
        end.setHours(23, 59, 59, 999);
        if (d > end) return false;
      }
      return true;
    });
  }, [reports, search, fAudience, fType, fLang, fFrom, fTo]);

  const clearFilters = () => {
    setSearch('');
    setFAudience('all');
    setFType('all');
    setFLang('all');
    setFFrom('');
    setFTo('');
  };

  const fmtDate = (r) => {
    const d = toDate(r.generated_at) || (r.date ? new Date(r.date) : null);
    return d ? d.toLocaleString() : '-';
  };

  const renderActions = (r) => (
    <Stack direction="row" spacing={0.5}>
      <IconButton size="small" href={r.pdf_url} target="_blank" rel="noopener noreferrer" disabled={!r.pdf_url} title="Abrir PDF">
        <PictureAsPdfIcon fontSize="small" color={r.pdf_url ? 'error' : 'disabled'} />
      </IconButton>
      <IconButton size="small" href={r.audio_url} target="_blank" rel="noopener noreferrer" disabled={!r.audio_url} title="Abrir áudio">
        <GraphicEqIcon fontSize="small" color={r.audio_url ? 'primary' : 'disabled'} />
      </IconButton>
    </Stack>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: FIDDUX_BRANDING.primary }}>
          Biblioteca de Reports
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={load} variant="outlined" size="small">Atualizar</Button>
      </Box>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Todos os reports gerados (PDF e áudio). Busque por título/assunto e filtre por audiência, tipo, idioma e data.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr', md: '2fr 1fr 1fr 1fr 1fr 1fr' }, gap: 1.5, alignItems: 'center' }}>
          <TextField label="Buscar título/assunto" value={search} onChange={(e) => setSearch(e.target.value)} size="small" fullWidth />
          <FormControl size="small" fullWidth>
            <InputLabel>Audiência</InputLabel>
            <Select value={fAudience} label="Audiência" onChange={(e) => setFAudience(e.target.value)}>
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="internal">Interno</MenuItem>
              <MenuItem value="client">Cliente</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Tipo</InputLabel>
            <Select value={fType} label="Tipo" onChange={(e) => setFType(e.target.value)}>
              <MenuItem value="all">Todos</MenuItem>
              {types.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Idioma</InputLabel>
            <Select value={fLang} label="Idioma" onChange={(e) => setFLang(e.target.value)}>
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="pt">PT</MenuItem>
              <MenuItem value="en">EN</MenuItem>
            </Select>
          </FormControl>
          <TextField label="De" type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Até" type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} />
        </Box>
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button size="small" onClick={clearFilters}>Limpar filtros</Button>
          <Typography variant="caption" color="text.secondary">{filtered.length} de {reports.length} reports</Typography>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Nenhum report encontrado.</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {filtered.map((r) => (
            <Card key={r.id} variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{r.title || r.filename}</Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {r.audience && <Chip size="small" color={audienceColor(r.audience)} label={AUDIENCE_LABELS[r.audience] || r.audience} />}
                  {r.report_type && <Chip size="small" variant="outlined" label={r.report_type} />}
                  {r.language && <Chip size="small" variant="outlined" label={String(r.language).toUpperCase()} />}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{fmtDate(r)}</Typography>
                {renderActions(r)}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Título</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Audiência</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Idioma</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.title || r.filename}</Typography>
                    {r.filename && r.title && (
                      <Typography variant="caption" color="text.secondary">{r.filename}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.audience ? <Chip size="small" color={audienceColor(r.audience)} label={AUDIENCE_LABELS[r.audience] || r.audience} /> : '-'}
                  </TableCell>
                  <TableCell>{r.report_type || '-'}</TableCell>
                  <TableCell>{r.language ? String(r.language).toUpperCase() : '-'}</TableCell>
                  <TableCell>{fmtDate(r)}</TableCell>
                  <TableCell align="right">{renderActions(r)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
